/**
 * Admin Evaluation Console — view the 4-track judgement on agent output.
 *
 * Daily digests: Track 1 (instruction-following), Track 2 (reasoning),
 * Track 3 (behavioral calibration). Track 4 (adversarial / trap replay)
 * runs weekly on a 25-trap set and lives in its own card.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCcw, Play, AlertTriangle, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

async function call(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try { payload = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    throw new Error(payload?.detail || `Request failed (${res.status})`);
  }
  return payload;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
};

const fmtDt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

function StatBlock({ label, value, sub, tone = "default", testid }) {
  const accent = tone === "accent" ? "text-[var(--accent)]" : "text-[var(--text)]";
  return (
    <div data-testid={testid} className="border border-[var(--border)] p-5">
      <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
        {label}
      </div>
      <div className={`font-serif text-4xl leading-none ${accent}`}>{value}</div>
      {sub && (
        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-3">
          {sub}
        </div>
      )}
    </div>
  );
}

function DigestRow({ digest, expanded, onToggle }) {
  const t1 = digest.t1 || {};
  const t2 = digest.t2 || {};
  const t3 = digest.t3 || {};
  const lowSigma = (t3.agents_with_low_sigma || []).length;
  return (
    <div className="border border-[var(--border)] mb-3" data-testid={`digest-row-${digest.id}`}>
      <button
        onClick={onToggle}
        data-testid={`digest-toggle-${digest.id}`}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[var(--bg-elevated)] transition-colors"
      >
        <div className="flex items-center gap-5">
          <ChevronRight
            size={14}
            className={`text-[var(--text-dim)] transition-transform ${expanded ? "rotate-90" : ""}`}
          />
          <div>
            <div className="font-serif text-base text-[var(--text)]">
              {fmtDate(digest.day_start)}
            </div>
            <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-1">
              {digest.total_judged} scored · {digest.processed} new
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6 font-mono-ui text-[10px] tracking-[0.2em] uppercase">
          <div className="text-right">
            <div className="text-[var(--text-dim)]">T1</div>
            <div className="text-[var(--text)] mt-1">{pct(t1.follow_rate)}</div>
          </div>
          <div className="text-right">
            <div className="text-[var(--text-dim)]">T2</div>
            <div className="text-[var(--text)] mt-1">{t2.avg_score ?? "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[var(--text-dim)]">σ</div>
            <div className="text-[var(--text)] mt-1">{t3.fleet_sigma ?? "—"}</div>
          </div>
          {lowSigma > 0 && (
            <AlertTriangle size={14} className="text-[var(--accent)]" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[var(--border)] p-5 space-y-6 bg-[var(--bg-elevated)]">
          {/* Per-agent table */}
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
              Per-agent
            </div>
            <div className="border border-[var(--border)] overflow-x-auto">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] border-b border-[var(--border)]">
                    <th className="text-left px-3 py-2 font-normal tracking-[0.18em] uppercase">agent</th>
                    <th className="text-right px-3 py-2 font-normal tracking-[0.18em] uppercase">n</th>
                    <th className="text-right px-3 py-2 font-normal tracking-[0.18em] uppercase">follow</th>
                    <th className="text-right px-3 py-2 font-normal tracking-[0.18em] uppercase">avg T2</th>
                    <th className="text-right px-3 py-2 font-normal tracking-[0.18em] uppercase">σ</th>
                  </tr>
                </thead>
                <tbody>
                  {(digest.per_agent || []).map((p) => (
                    <tr key={p.agent_id} className="border-b border-[var(--border-soft)] last:border-b-0">
                      <td className="px-3 py-2 text-[var(--text)]">{p.agent_id}</td>
                      <td className="px-3 py-2 text-right text-[var(--text-muted)]">{p.n}</td>
                      <td className="px-3 py-2 text-right text-[var(--text)]">{pct(p.follow_rate)}</td>
                      <td className="px-3 py-2 text-right text-[var(--text)]">{p.avg_t2}</td>
                      <td className={`px-3 py-2 text-right ${p.calibration_sigma < 0.3 ? "text-[var(--accent)]" : "text-[var(--text)]"}`}>
                        {p.calibration_sigma}
                      </td>
                    </tr>
                  ))}
                  {(!digest.per_agent || digest.per_agent.length === 0) && (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-center text-[var(--text-dim)]">
                        No agent data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lowlights */}
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-3">
              Lowlights
            </div>
            {(digest.lowlights || []).length === 0 ? (
              <div className="font-mono-ui text-[11px] text-[var(--text-dim)]">
                None — all chats above floor.
              </div>
            ) : (
              <ul className="space-y-2">
                {(digest.lowlights || []).map((lw, i) => (
                  <li key={i} className="font-mono-ui text-[11px] text-[var(--text-muted)] flex flex-wrap gap-2">
                    <Link
                      to={`/admin/agents?conv=${lw.conversation_id}`}
                      data-testid={`lowlight-link-${i}`}
                      className="text-[var(--accent)] hover:underline"
                    >
                      {lw.agent_id}
                    </Link>
                    <span className="text-[var(--text-dim)]">T1={lw.t1}</span>
                    <span className="text-[var(--text-dim)]">T2={lw.t2}</span>
                    <span className="text-[var(--text)]">— {lw.reason || "weak reasoning"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Email log */}
          <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
            {digest.email_result?.ok
              ? `Email sent to ${(digest.recipients || []).join(", ")}`
              : `Email: ${digest.email_result?.reason || "not sent"}`}
          </div>
        </div>
      )}
    </div>
  );
}

function AdversarialCard({ run, onOpen }) {
  if (!run) {
    return (
      <div className="border border-[var(--border)] p-6">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-2">
          Track 4 · Adversarial
        </div>
        <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed">
          No trap run yet. The weekly cron fires Sundays at 06:00 UTC, or run
          the 25-trap set on demand.
        </p>
      </div>
    );
  }
  return (
    <div className="border border-[var(--border)] p-6" data-testid="adversarial-summary">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
            Track 4 · Adversarial — last run
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-1">
            {fmtDt(run.run_at)}
          </div>
        </div>
        <button
          onClick={onOpen}
          data-testid="adversarial-open-details"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
        >
          Details
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-[var(--border)] p-3">
          <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">Pushback</div>
          <div className="font-serif text-2xl text-[var(--text)] mt-1">{pct(run.pushback_rate)}</div>
          <div className="font-mono-ui text-[9px] text-[var(--text-dim)] mt-1">
            {run.pushback_n} / {run.total}
          </div>
        </div>
        {(run.by_category || []).map((c) => (
          <div key={c.category} className="border border-[var(--border)] p-3">
            <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
              {c.category.replace(/_/g, " ")}
            </div>
            <div className="font-serif text-2xl text-[var(--text)] mt-1">{pct(c.rate)}</div>
            <div className="font-mono-ui text-[9px] text-[var(--text-dim)] mt-1">
              {c.pushed} / {c.n}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdversarialDetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    call(`/admin/agents/evaluation/adversarial/runs/${id}`)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [id]);

  return (
    <div
      data-testid="adversarial-modal"
      className="fixed inset-0 bg-[var(--text)]/70 z-50 flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
              Adversarial run
            </div>
            <div className="font-serif text-lg text-[var(--text)] mt-1">
              {data ? fmtDt(data.run_at) : "Loading…"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="adversarial-modal-close"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--text)]"
          >
            Close
          </button>
        </header>
        <div className="p-6">
          {err && <div className="text-red-400 font-mono-ui text-[11px]">{err}</div>}
          {data && (
            <div className="space-y-3">
              {(data.results || []).map((r) => (
                <div
                  key={r.trap_id}
                  className={`border p-4 ${r.pushed_back ? "border-[var(--border)]" : "border-red-500/60"}`}
                  data-testid={`trap-row-${r.trap_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
                      {r.category.replace(/_/g, " ")} · {r.trap_id}
                    </span>
                    <span className={`font-mono-ui text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 border ${r.pushed_back ? "border-[var(--accent)] text-[var(--accent)]" : "border-red-500/60 text-red-400"}`}>
                      {r.pushed_back ? "pushed back" : "folded"}
                    </span>
                  </div>
                  <div className="font-sans text-xs text-[var(--text-muted)] mb-2 italic">
                    “{r.prompt}”
                  </div>
                  <div className="font-sans text-sm text-[var(--text)] whitespace-pre-wrap">
                    {r.answer}
                  </div>
                  {r.reason && (
                    <div className="font-mono-ui text-[10px] text-[var(--text-dim)] mt-2 border-t border-[var(--border-soft)] pt-2">
                      judge: {r.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminEvaluation() {
  const { user } = useAuth();
  const [digests, setDigests] = useState([]);
  const [adv, setAdv] = useState(null);
  const [advList, setAdvList] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [advDetailId, setAdvDetailId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [advBusy, setAdvBusy] = useState(false);
  const [runDate, setRunDate] = useState("");

  const reload = async () => {
    try {
      const [d, a] = await Promise.all([
        call("/admin/agents/evaluation/digests?limit=30"),
        call("/admin/agents/evaluation/adversarial/runs?limit=10"),
      ]);
      setDigests(d || []);
      setAdvList(a || []);
      setAdv((a && a[0]) || null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") reload();
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/admin/evaluation" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const today = digests[0] || null;

  const onRunEval = async () => {
    setBusy(true);
    try {
      const r = await call(
        `/admin/agents/evaluation/run${runDate ? `?date=${runDate}` : ""}`,
        { method: "POST" },
      );
      if (r.queued) {
        toast.info(r.message || "Evaluation started. Refresh in ~2 min.");
      } else if (r.email?.ok) {
        toast.success(`Eval emailed · ${r.total_judged} scored`);
      } else {
        toast.info(`Eval ran · ${r.total_judged} scored · ${r.email?.reason || "no email"}`);
      }
      await reload();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onRunAdv = async () => {
    setAdvBusy(true);
    try {
      const r = await call("/admin/agents/evaluation/adversarial/run", { method: "POST" });
      if (r.queued) {
        toast.info(r.message || "Trap replay started. Refresh in ~3 min.");
      } else {
        toast.success(`Adversarial run · pushback ${pct(r.pushback_rate)} (${r.pushback_n}/${r.total})`);
      }
      await reload();
    } catch (e) { toast.error(e.message); }
    finally { setAdvBusy(false); }
  };

  const t1 = today?.t1 || {};
  const t2 = today?.t2 || {};
  const t3 = today?.t3 || {};

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/admin/agents"
          data-testid="evaluation-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Agents
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">Evaluation</h1>
        <button
          onClick={reload}
          data-testid="evaluation-refresh"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-2"
        >
          <RefreshCcw size={12} /> Refresh
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        {/* Hero */}
        <section className="py-10 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)] mb-2">
            Four-track judgement
          </div>
          <h2 className="font-serif text-3xl text-[var(--text)] mb-2">
            Is HBM saying anything different from the optimistic-AI default?
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] max-w-2xl leading-relaxed">
            Track 1: did HBM answer the question. Track 2: did it reason or
            hedge. Track 3: does each agent vary across topics or has its voice
            collapsed. Track 4: does HBM push back on a 25-trap adversarial set
            (weekly). Daily eval email sends at 05:00 UTC.
          </p>

          {/* Run controls */}
          <div className="flex flex-wrap items-end gap-3 mt-7">
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] block mb-2">
                Run for date (UTC)
              </label>
              <input
                type="date"
                value={runDate}
                onChange={(e) => setRunDate(e.target.value)}
                data-testid="eval-run-date"
                className="bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 font-mono-ui text-xs text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
              />
            </div>
            <button
              onClick={onRunEval}
              disabled={busy}
              data-testid="eval-run-btn"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
            >
              <Play size={12} /> {busy ? "Running…" : "Run + email now"}
            </button>
            <button
              onClick={onRunAdv}
              disabled={advBusy}
              data-testid="adv-run-btn"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
            >
              <Play size={12} /> {advBusy ? "Running traps…" : "Run trap set"}
            </button>
          </div>
        </section>

        {/* Latest day snapshot */}
        <section className="py-10 border-b border-[var(--border)]">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
                Latest
              </div>
              <div className="font-serif text-xl text-[var(--text)] mt-1">
                {today ? fmtDate(today.day_start) : "No data yet"}
              </div>
            </div>
            <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
              {today ? `${today.total_judged} chats scored` : ""}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <StatBlock
              label="T1 · Instruction follow"
              value={today ? pct(t1.follow_rate) : "—"}
              sub={today ? `${t1.follow_n || 0} answered · ${t1.fail_n || 0} drifted` : ""}
              tone="accent"
              testid="stat-t1"
            />
            <StatBlock
              label="T2 · Reasoning (avg / 5)"
              value={today ? (t2.avg_score ?? "—") : "—"}
              sub={today ? `${t2.low_count || 0} below floor (1.5)` : ""}
              testid="stat-t2"
            />
            <StatBlock
              label="T3 · Voice σ (fleet)"
              value={today ? (t3.fleet_sigma ?? "—") : "—"}
              sub={
                today
                  ? (t3.agents_with_low_sigma || []).length
                    ? `Collapse risk: ${(t3.agents_with_low_sigma || []).join(", ")}`
                    : "All agents varying across topics"
                  : ""
              }
              testid="stat-t3"
            />
          </div>
        </section>

        {/* Adversarial */}
        <section className="py-10 border-b border-[var(--border)]">
          <AdversarialCard run={adv} onOpen={() => setAdvDetailId(adv?.id)} />
          {advList.length > 1 && (
            <div className="mt-4">
              <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)] mb-2">
                Earlier runs
              </div>
              <ul className="space-y-1">
                {advList.slice(1).map((r) => (
                  <li key={r.id} className="flex items-center justify-between border border-[var(--border)] px-4 py-2">
                    <span className="font-mono-ui text-[11px] text-[var(--text-muted)]">
                      {fmtDt(r.run_at)}
                    </span>
                    <button
                      onClick={() => setAdvDetailId(r.id)}
                      data-testid={`adv-row-${r.id}`}
                      className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)]"
                    >
                      pushback {pct(r.pushback_rate)} →
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* History */}
        <section className="py-10">
          <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] mb-5">
            History
          </div>
          {digests.length === 0 ? (
            <div className="border border-[var(--border)] p-8 text-center font-mono-ui text-[11px] text-[var(--text-dim)]">
              No evaluations yet. Run one above, or wait for the 05:00 UTC cron.
            </div>
          ) : (
            digests.map((d) => (
              <DigestRow
                key={d.id}
                digest={d}
                expanded={expanded === d.id}
                onToggle={() => setExpanded(expanded === d.id ? null : d.id)}
              />
            ))
          )}
        </section>
      </main>

      {advDetailId && (
        <AdversarialDetailModal id={advDetailId} onClose={() => setAdvDetailId(null)} />
      )}
    </div>
  );
}
